import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type SetPreferredTabKey = (key: string | undefined) => void;
type SetEmbedIntent = (value: boolean) => void;

const NOOP_SET_PREFERRED_TAB_KEY: SetPreferredTabKey = () => {
	// Intentional no-op: safe default/reset used as the context fallback and the
	// module-level setter when no provider is mounted. Setting a tab preference
	// without a mounted provider should silently do nothing rather than throw.
};

const NOOP_SET_EMBED_INTENT: SetEmbedIntent = () => {
	// Intentional no-op: safe default/reset used as the context fallback and the
	// module-level setter when no provider is mounted.
};

/**
 * Shape of the RovoLinkPicker tab-preference context.
 *
 * `preferredTabKey` is consumed by `RovoLinkPickerAdapter` so the link picker
 * can auto-select a particular tab the first time tabs load. It is meant to
 * be a one-shot signal: callers that set it should rely on the adapter to
 * clear it once the picker has been used (submit/cancel).
 *
 * `embedIntent` is a session-scoped, one-shot signal set by the quick-insert
 * embed action closures (the `/embed` item and the per-app items). It records
 * that the picker was opened from a quick-insert embed entry point so that the
 * adapter can request an embed appearance for the single link the user inserts.
 * Like `preferredTabKey`, the adapter clears it on submit/cancel/unmount.
 */
interface RovoLinkPickerTabPreferenceContextValue {
	preferredTabKey: string | undefined;
	setPreferredTabKey: SetPreferredTabKey;
	embedIntent: boolean;
	setEmbedIntent: SetEmbedIntent;
}

const RovoLinkPickerTabPreferenceContext = createContext<RovoLinkPickerTabPreferenceContextValue>({
	preferredTabKey: undefined,
	setPreferredTabKey: NOOP_SET_PREFERRED_TAB_KEY,
	embedIntent: false,
	setEmbedIntent: NOOP_SET_EMBED_INTENT,
});

/**
 * Module-level registry of the setters from every mounted provider, in mount
 * order. This exists so non-React callers (specifically quick-insert action
 * closures inside `ExtensionManifest`s, which run outside the render tree)
 * can read the up-to-date setter without re-creating the manifest on every
 * render.
 *
 * The most recently mounted provider wins. Several providers can be mounted at
 * once (e.g. the page editor plus a comment editor), so when one unmounts the
 * registry falls back to the provider that is still mounted instead of a no-op.
 *
 * Providers keep this in sync via `useEffect`; consumers must read the setter
 * at action-execution time via `getSetPreferredTabKey()`.
 */
const preferredTabKeySetters: SetPreferredTabKey[] = [];

/**
 * Registry of `setEmbedIntent` setters from every mounted provider. Mirrors
 * `preferredTabKeySetters` so the non-React quick-insert action closures can
 * record embed intent without re-creating the manifest on every render.
 */
const embedIntentSetters: SetEmbedIntent[] = [];

const registerSetter = <T,>(registry: T[], setter: T): (() => void) => {
	registry.push(setter);
	return () => {
		const index = registry.lastIndexOf(setter);
		if (index !== -1) {
			registry.splice(index, 1);
		}
	};
};

/**
 * Returns the most recently registered `setPreferredTabKey`. Safe to call from
 * outside the React render tree (e.g. inside a quick-insert `action` closure).
 * Returns a no-op when no provider is mounted.
 */
export const getSetPreferredTabKey = (): SetPreferredTabKey =>
	preferredTabKeySetters[preferredTabKeySetters.length - 1] ?? NOOP_SET_PREFERRED_TAB_KEY;

/**
 * Returns the most recently registered `setEmbedIntent`. Safe to call from
 * outside the React render tree (e.g. inside a quick-insert `action` closure).
 * Returns a no-op when no provider is mounted.
 */
export const getSetEmbedIntent = (): SetEmbedIntent =>
	embedIntentSetters[embedIntentSetters.length - 1] ?? NOOP_SET_EMBED_INTENT;

interface RovoLinkPickerTabPreferenceProviderProps {
	children: React.ReactNode;
}

export const RovoLinkPickerTabPreferenceProvider = ({
	children,
}: RovoLinkPickerTabPreferenceProviderProps): React.JSX.Element => {
	const [preferredTabKey, setPreferredTabKeyState] = useState<string | undefined>(undefined);
	const [embedIntent, setEmbedIntentState] = useState<boolean>(false);

	const setPreferredTabKey = useCallback<SetPreferredTabKey>((key) => {
		setPreferredTabKeyState(key);
	}, []);

	const setEmbedIntent = useCallback<SetEmbedIntent>((value) => {
		setEmbedIntentState(value);
	}, []);

	useEffect(() => registerSetter(preferredTabKeySetters, setPreferredTabKey), [setPreferredTabKey]);

	useEffect(() => registerSetter(embedIntentSetters, setEmbedIntent), [setEmbedIntent]);

	const value = useMemo<RovoLinkPickerTabPreferenceContextValue>(
		() => ({ preferredTabKey, setPreferredTabKey, embedIntent, setEmbedIntent }),
		[preferredTabKey, setPreferredTabKey, embedIntent, setEmbedIntent],
	);

	return (
		<RovoLinkPickerTabPreferenceContext.Provider value={value}>
			{children}
		</RovoLinkPickerTabPreferenceContext.Provider>
	);
};

export const useRovoLinkPickerTabPreference = (): RovoLinkPickerTabPreferenceContextValue =>
	useContext(RovoLinkPickerTabPreferenceContext);
